import React, { useState, useEffect } from 'react';
import { Label, TextField } from "@fluentui/react";
import { FiHelpCircle } from 'react-icons/fi'
import styles from "./ResponseTempSlider.module.css";

interface Props {
  className?: string;
  onChange: (newValue: number) => void;
  value?: number;
  useDarkMode: boolean;
}

const tooltipHtml = `
This parameter determines the creativity and diversity of the text generated by the GPT model.<br /><br />
A higher temperature value (e.g., 1.0) leads to more diverse and creative text, while a lower value
(e.g., 0.0) results in more focused and deterministic text.`;


export const ResponseTempSlider = ({ className, onChange, value = 0, useDarkMode }: Props) => {
  const [sliderValue, setSliderValue] = useState<number>(value);
  const [displayValue, setDisplayValue] = useState<string>(value.toFixed(2));


  useEffect(() => {
    setSliderValue(value);
    setDisplayValue(value.toString()); // Keep the full precision when value is set from outside
  }, [value]);

  const updateValue = (newValue: number, fromSlider: boolean = false) => {
    const boundedValue = Math.max(0, Math.min(newValue, 1));
    setSliderValue(boundedValue);
    // If the update comes from the slider, round to 2 decimal places
    setDisplayValue(fromSlider ? boundedValue.toFixed(2) : newValue.toString());
    onChange(boundedValue);
  };

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value);
    updateValue(newValue, true);
    setSliderValue(newValue);
    setDisplayValue(newValue.toFixed(2));
    onChange(newValue);
  };

  const handleInputChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    if (newValue !== undefined) {
      setDisplayValue(newValue); // Just update the display value, do not round
    }
  };

  const handleInputBlur = () => {
    // When the input field loses focus, validate and potentially round the number
    let numericValue = parseFloat(displayValue);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 1) {
      numericValue = sliderValue; // Revert to the last valid value from the slider
    }
    updateValue(numericValue); // Do not round to 2 decimal places here
  };

  return (
    <div className={`${styles.container} ${className ?? ""}`}>
      <Label>Response Temperature&nbsp;
        <FiHelpCircle
          data-tooltip-id="ResponseTempSlider-tooltip"
          data-tooltip-html={tooltipHtml}
        />
      </Label>
      <TextField
        id="responseTempSlider"
        className={styles.responseTempValue}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
      />
      <input
        className={useDarkMode ? styles.sliderDark : styles.slider}
        type="range"
        value={sliderValue.toString()} // Convert to string for the input element
        step={0.01}
        min={0.00}
        max={1.00}
        onChange={handleSliderChange}
      />
    </div>
  );
};